import {
  RemoveTripsPostBookingAutomatedMessageDocument,
  UpsertPostBookingAutomatedMessageInput,
  UpsertTripsPostBookingAutomatedMessageDocument,
} from '@website/types/graphqlOperations';
import { useForm } from 'react-hook-form';
import { Button, Col, Form } from 'react-bootstrap';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { AlertDismissible } from '@website/components/utilities/AlertDismissible';
import { FaSpinner, FaTrashAlt } from 'react-icons/fa';
import { preventURLsValidator } from '@website/libs/validator/preventURLsValidator';

export function DeleteAutomatedMessageButton({
  tripID,
  onDelete,
}: {
  tripID: string;
  onDelete: () => void;
}) {
  // const [isDeleting, setIsDeleting] = useState(false);

  const [removeAutomatedMessage, { loading: isDeleting }] = useMutation(
    RemoveTripsPostBookingAutomatedMessageDocument,
  );

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const removeMessage = async (tripID: string) => {
    try {
      await removeAutomatedMessage({
        variables: {
          tripID,
        },
      });
      onDelete();
    } catch (e) {
      if (e.networkError) {
        alert(
          'Internet connection lost - please retry when you are connected.',
        );
      } else {
        alert(e);
      }
    }
  };

  return (
    <Button
      onClick={() => removeMessage(tripID)}
      variant="danger"
      type="button"
    >
      <span className="p-1">{isDeleting ? <FaSpinner /> : <FaTrashAlt />}</span>
      <span className="p-1">Delete Automated Message</span>
    </Button>
  );
}

type Props = {
  tripID: string;
  automatedMessage?: string;
};

export default function PostBookingAutomatedMessageForm({
  tripID,
  automatedMessage,
}: Props) {
  const [upsertAutomatedMessage] = useMutation(
    UpsertTripsPostBookingAutomatedMessageDocument,
  );

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<UpsertPostBookingAutomatedMessageInput>({
    mode: 'onChange',
    defaultValues: {
      message: automatedMessage,
      tripID,
    },
  });

  const submitForm = async (
    formData: UpsertPostBookingAutomatedMessageInput,
  ) => {
    try {
      await upsertAutomatedMessage({
        variables: {
          input: formData,
        },
      });
      setShowSuccessAlert(true);
    } catch (e) {
      if (e.networkError) {
        alert(
          'Internet connection lost - please retry when you are connected.',
        );
      } else {
        alert(e);
      }
    }
  };

  return (
    <>
      {showSuccessAlert && (
        <AlertDismissible
          variant="success"
          title="Automated Message"
          message="Message saved successfully"
          onClose={() => setShowSuccessAlert(false)}
        />
      )}
      <Form onSubmit={handleSubmit(submitForm)}>
        <Form.Group controlId="message">
          <Form.Label>
            Message to be sent to guests after making a booking
          </Form.Label>
          <Form.Control
            name="message"
            rows={5}
            isInvalid={!!errors.message}
            as="textarea"
            {...register('message', {
              required: true,
              validate: preventURLsValidator,
            })}
          />

          {errors.message && errors.message.type === 'validate' && (
            <Form.Control.Feedback type="invalid">
              Please don't send links or emails. All trip information should be
              setup on the trip page (e.g. with FAQs), and all communication
              should be through the Skyhook platform.
            </Form.Control.Feedback>
          )}

          {errors.message && errors.message.type === 'required' && (
            <Form.Control.Feedback type="invalid">
              Please enter the automated message you would like to send.
            </Form.Control.Feedback>
          )}
        </Form.Group>

        <Col className="mt-3 mb-3 d-grid gap-2">
          <SubmitButton isSubmitting={isSubmitting} text="Save Message" block />

          <DeleteAutomatedMessageButton
            tripID={tripID}
            onDelete={() => reset()}
          />
        </Col>
      </Form>
    </>
  );
}
